/**
 * Application and stack route constants.
 *
 * Contains application routes (root, details, project-nested applications,
 * create) and stack routes (nested under org -> project paths), plus helper
 * functions for generating parameterized paths.
 */

import { PREFIXES, buildPath, RouteWithPath } from "./routes-platform";

// Application route types
export interface ApplicationDetailRoute extends RouteWithPath {
  getPath: (appAlias: string) => string;
}

export interface ProjectApplicationsRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string) => string;
}

export interface ProjectApplicationDetailRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string, appAlias: string) => string;
}

export interface ApplicationCreateRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string) => string;
}

export interface ApplicationRoutes {
  ROOT: RouteWithPath;
  DETAILS: ApplicationDetailRoute;
  PROJECT_APPLICATIONS: ProjectApplicationsRoute;
  PROJECT_APPLICATION_DETAILS: ProjectApplicationDetailRoute;
  CREATE: ApplicationCreateRoute;
}

// Application routes with metadata
export const APPLICATIONS: ApplicationRoutes = {
  ROOT: {
    path: PREFIXES.APPLICATIONS,
    title: "Applications",
  },
  DETAILS: {
    path: `${PREFIXES.APPLICATIONS}/:appAlias`,
    title: "Application Details",

    // Helper function to generate path with params
    getPath: (appAlias: string) =>
      buildPath(`${PREFIXES.APPLICATIONS}/:appAlias`, { appAlias }),
  },
  PROJECT_APPLICATIONS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/applications`,
    title: "Project Applications",

    // Helper function to generate path with params
    getPath: (orgAlias: string, projectAlias: string) =>
      buildPath(
        `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/applications`,
        { orgAlias, projectAlias },
      ),
  },
  PROJECT_APPLICATION_DETAILS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/applications/:appAlias`,
    title: "Project Application Details",

    // Helper function to generate path with params
    getPath: (orgAlias: string, projectAlias: string, appAlias: string) =>
      buildPath(
        `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/applications/:appAlias`,
        { orgAlias, projectAlias, appAlias },
      ),
  },
  CREATE: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/applications/create`,
    title: "Create Application",

    // Helper function to generate path with params
    getPath: (orgAlias: string, projectAlias: string) =>
      buildPath(
        `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/applications/create`,
        { orgAlias, projectAlias },
      ),
  },
};

// Stack route types (nested under org -> project paths)
export interface StackDetailsRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string, sysAlias: string) => string;
}

export interface StackCreateRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string) => string;
}

export interface StackEditRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string, sysAlias: string) => string;
}

export interface StackAdoptRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string) => string;
}

export interface StackServicesRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string, sysAlias: string) => string;
}

export interface StackRoutes {
  DETAILS: StackDetailsRoute;
  CREATE: StackCreateRoute;
  EDIT: StackEditRoute;
  ADOPT: StackAdoptRoute;
  SERVICES: StackServicesRoute;
}

const STACKS_BASE = `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/stacks`;

export const STACKS: StackRoutes = {
  DETAILS: {
    path: `${STACKS_BASE}/:sysAlias`,
    title: "Stack Details",
    getPath: (orgAlias, projectAlias, sysAlias) =>
      buildPath(`${STACKS_BASE}/:sysAlias`, {
        orgAlias,
        projectAlias,
        sysAlias,
      }),
  },
  CREATE: {
    path: `${STACKS_BASE}/create`,
    title: "Create Stack",
    getPath: (orgAlias, projectAlias) =>
      buildPath(`${STACKS_BASE}/create`, { orgAlias, projectAlias }),
  },
  EDIT: {
    path: `${STACKS_BASE}/:sysAlias/edit`,
    title: "Edit Stack",
    getPath: (orgAlias, projectAlias, sysAlias) =>
      buildPath(`${STACKS_BASE}/:sysAlias/edit`, {
        orgAlias,
        projectAlias,
        sysAlias,
      }),
  },
  ADOPT: {
    path: `${STACKS_BASE}/adopt`,
    title: "Adopt Existing Project",
    getPath: (orgAlias, projectAlias) =>
      buildPath(`${STACKS_BASE}/adopt`, { orgAlias, projectAlias }),
  },
  SERVICES: {
    path: `${STACKS_BASE}/:sysAlias/services`,
    title: "Review Detected Services",
    getPath: (orgAlias, projectAlias, sysAlias) =>
      buildPath(`${STACKS_BASE}/:sysAlias/services`, {
        orgAlias,
        projectAlias,
        sysAlias,
      }),
  },
};

// Helper functions for route generation
export const getStackDetailsPath = (
  orgAlias: string,
  projectAlias: string,
  sysAlias: string,
) => STACKS.DETAILS.getPath(orgAlias, projectAlias, sysAlias);
export const getStackCreatePath = (orgAlias: string, projectAlias: string) =>
  STACKS.CREATE.getPath(orgAlias, projectAlias);
